import { TaskTimelineFrameEnum } from 'graphql-common';
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  isAfter,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  addMonthsFn,
  addQuartersFn,
  addWeeksFn,
  addYearsFn,
  CET,
  revertTransformDate,
  subMonthsFn,
  subQuartersFn,
  subWeeksFn,
  subYearsFn,
} from '@lib/utils/dateTimeHelpers';

export const getPeriodStartDate = (
  dateValue: string,
  view: TaskTimelineFrameEnum,
) => {
  const date = toZonedTime(parseISO(dateValue), CET);
  let periodStartDate = startOfDay(date);

  switch (view) {
    case TaskTimelineFrameEnum.Year:
      periodStartDate = startOfYear(date);
      break;
    case TaskTimelineFrameEnum.Quarter:
      periodStartDate = startOfQuarter(date);
      break;
    case TaskTimelineFrameEnum.Month:
      periodStartDate = startOfMonth(date);
      break;
    case TaskTimelineFrameEnum.Week:
      periodStartDate = startOfWeek(date, { weekStartsOn: 1 }); // Monday as the start of the week
      break;
    default:
      break;
  }

  return periodStartDate;
};

export const getPeriodEndDate = (
  dateValue: string,
  view: TaskTimelineFrameEnum,
) => {
  const date = toZonedTime(parseISO(dateValue), CET);
  let periodEndDate = endOfDay(date);

  switch (view) {
    case TaskTimelineFrameEnum.Year:
      periodEndDate = endOfYear(date);
      break;
    case TaskTimelineFrameEnum.Quarter:
      periodEndDate = endOfQuarter(date);
      break;
    case TaskTimelineFrameEnum.Month:
      periodEndDate = endOfMonth(date);
      break;
    case TaskTimelineFrameEnum.Week:
      periodEndDate = endOfWeek(date, { weekStartsOn: 1 }); // Sunday as the end of the week
      break;
    default:
      break;
  }

  return periodEndDate;
};

export function getDecrementedNextEndDate(
  nextEndDate: Date,
  view: TaskTimelineFrameEnum,
) {
  if (view === TaskTimelineFrameEnum.Year) {
    return subYearsFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Quarter) {
    return subQuartersFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Month) {
    return subMonthsFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Week) {
    return subWeeksFn(nextEndDate, 1);
  }
  return undefined;
}

export function getIncrementedNextEndDate(
  nextEndDate: Date,
  view: TaskTimelineFrameEnum,
) {
  if (view === TaskTimelineFrameEnum.Year) {
    return addYearsFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Quarter) {
    return addQuartersFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Month) {
    return addMonthsFn(nextEndDate, 1);
  }
  if (view === TaskTimelineFrameEnum.Week) {
    return addWeeksFn(nextEndDate, 1);
  }
  return undefined;
}

export function getMinDate(
  periodStartParam: string | null | undefined,
  view: TaskTimelineFrameEnum,
) {
  if (periodStartParam) {
    const comparedDate = new Date(periodStartParam);
    if (view === TaskTimelineFrameEnum.Week) {
      return startOfWeek(comparedDate, { weekStartsOn: 1 });
    }
    if (view === TaskTimelineFrameEnum.Month) {
      return startOfMonth(comparedDate);
    }
    if (view === TaskTimelineFrameEnum.Quarter) {
      return startOfQuarter(comparedDate);
    }
    if (view === TaskTimelineFrameEnum.Year) {
      return startOfYear(comparedDate);
    }
  }
  return undefined;
}

export function getMaxDate(
  periodEndParam: string | null | undefined,
  view: TaskTimelineFrameEnum,
) {
  const date = periodEndParam
    ? new Date(periodEndParam)
    : new Date().toISOString();
  const comparedDate = new Date(date);
  if (view === TaskTimelineFrameEnum.Week) {
    return endOfWeek(comparedDate, { weekStartsOn: 1 });
  }
  if (view === TaskTimelineFrameEnum.Month) {
    return endOfMonth(comparedDate);
  }
  if (view === TaskTimelineFrameEnum.Quarter) {
    return endOfQuarter(comparedDate);
  }
  if (view === TaskTimelineFrameEnum.Year) {
    return endOfYear(comparedDate);
  }
  return new Date();
}

export function getIsDecrementDisabled(
  endDate: string | null | undefined,
  periodStartParam: string | null | undefined,
  view: TaskTimelineFrameEnum,
) {
  if (endDate && periodStartParam) {
    const nextEndDate = getDecrementedNextEndDate(
      new Date(revertTransformDate(endDate)),
      view,
    );
    if (!nextEndDate) return false;

    // Calculate the period end of the decremented period
    const nextPeriodEnd = getPeriodEndDate(nextEndDate.toISOString(), view);

    // Disable if the entire decremented period ends before available data starts
    return (
      differenceInCalendarDays(
        nextPeriodEnd,
        revertTransformDate(periodStartParam),
      ) < 0
    );
  }
  return false;
}

export function getIsIncrementDisabled(
  endDate: string | null | undefined,
  periodEndParam: string | null | undefined,
  view: TaskTimelineFrameEnum,
) {
  if (endDate && periodEndParam) {
    const nextEndDate = getIncrementedNextEndDate(
      new Date(revertTransformDate(endDate)),
      view,
    );
    let comparedDate = new Date(periodEndParam);
    if (view === TaskTimelineFrameEnum.Week) {
      comparedDate = endOfWeek(comparedDate, { weekStartsOn: 1 });
    } else if (view === TaskTimelineFrameEnum.Month) {
      comparedDate = endOfMonth(comparedDate);
    } else if (view === TaskTimelineFrameEnum.Quarter) {
      comparedDate = endOfQuarter(comparedDate);
    } else if (view === TaskTimelineFrameEnum.Year) {
      comparedDate = endOfYear(comparedDate);
    }
    return nextEndDate ? isAfter(nextEndDate, comparedDate) : false;
  }
  return !(
    endDate &&
    differenceInCalendarDays(
      new Date(),
      new Date(revertTransformDate(endDate)),
    ) > 0
  );
}
